/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function isAwaitingOptionSelection() {
    const selects = [...document.querySelectorAll('.product-form select[name^="option"]')];
    if (selects.length) {
        return selects.some(function(select) {
            return select.options.length > 1 && !select.value;
        });
    }

    return [...document.querySelectorAll('.product-form .select__selected-value')].some(function(el) {
        return el.textContent.trim() === 'Choose an option';
    });
}

function clearSoldOutLabelWhileChoosing() {
    if (!isAwaitingOptionSelection()) return;

    document.querySelectorAll('[data-product-label-list] .label--subdued').forEach(function(el) {
        el.remove();
    });
}

document.addEventListener('variant:changed', function(event) {
    let variant = event.detail.variant;
    let variantSku = variant.sku;
    let skuElement = document.querySelector('[data-pdp-product-sku]');

    if (skuElement != null) {
        skuElement.textContent = variantSku;
    }

    clearSoldOutLabelWhileChoosing();
});

document.addEventListener('DOMContentLoaded', clearSoldOutLabelWhileChoosing);

function getVariantIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('variant');
}

function showVariantMessageCustomInventory(variantId) {
    document.querySelectorAll('.variant-custom-inventory-message').forEach(function(div) {
        div.classList.add('hidden');
    });

    if (variantId) {
        const messageDiv = document.querySelector(`.variant-custom-inventory-message[data-variant-id="${variantId}"]`);
        if (messageDiv) {
            setTimeout(() => {
                if (document.querySelector('[data-multiple-variants-available]')) {
                    const selectValues = document.querySelectorAll('.product .select__selected-value');
                    if ([...selectValues].every(el => el.textContent.trim() !== "Choose an option")) {
                        messageDiv.classList.remove('hidden');
                    }
                } else {
                    messageDiv.classList.remove('hidden');
                }
            }, 300);
        }
    }
}

document.addEventListener('DOMContentLoaded', function () {
    const inventoryMessage = document.querySelector('.variant-custom-inventory-message');

    if (!inventoryMessage) return;

    let variantId = getVariantIdFromUrl();

    if (!variantId) {
        const firstAvailableVariant = document.querySelector('[data-first-available-variant]');
        variantId = firstAvailableVariant ? firstAvailableVariant.getAttribute('data-first-available-variant') : null;
    }

    if (variantId) {
        showVariantMessageCustomInventory(variantId);
    }
});

function syncSelectWithInput(selectAttr, inputAttr) {
    const selectElement = document.querySelector(`.select-wrapper [${selectAttr}]`);
    const inputElement = document.querySelector(`.product-form__buy-buttons form [${inputAttr}]`);

    if (selectElement && inputElement) {
        selectElement.addEventListener('change', function() {
            inputElement.value = selectElement.value;
        });
    }
}

document.addEventListener("DOMContentLoaded", function (event) {
    if (document.querySelector('.combo-box__option-item')) {
        const pdpComboBoxButtons = document.querySelectorAll('.combo-box__option-item');

        pdpComboBoxButtons.forEach(button => {
            button.addEventListener('click', () => {
                setTimeout(() => {
                    const selectValues = document.querySelectorAll('.product .select__selected-value');
                    const selectedTexts = [...selectValues].map(el => el.textContent.trim());

                    if (selectedTexts.every(value => value !== "Choose an option")) {
                        const joinedValues = selectedTexts.join(" / ");
                        const findedVariantId = document.querySelector(`[data-attribute-variant-options="${joinedValues}"]`).getAttribute('data-variant-id');
                        showVariantMessageCustomInventory(findedVariantId);
                    }
                }, 300);
            });
        });
    }
});


// Sync crown and brim options with their respective inputs
syncSelectWithInput('data-crown-options', 'data-crown-input');
syncSelectWithInput('data-brim-options', 'data-brim-input');

// Fast Simon product links include ?variant= for the first variant.
// Style-number search often lands on a sold-out size, which shows "Sold out"
// before Size and Inseam are chosen. Department pages do not pass a variant,
// so strip it here and open the product with "Choose an option".
function productUrlWithoutVariant(oldUrl) {
    if (!oldUrl) return oldUrl;

    let newUrl = oldUrl.replace(/\/collections\/[a-zA-Z0-9-]+\//g, '/');
    const absolute = /^https?:\/\//i.test(newUrl);

    try {
        const url = new URL(newUrl, window.location.origin);
        url.searchParams.delete('variant');
        newUrl = absolute ? url.toString() : (url.pathname + url.search + url.hash);
    } catch (e) {
        newUrl = newUrl.replace(/([?&])variant=\d+(&?)/, (_, sep, trailing) => {
            if (sep === '?' && trailing === '&') return '?';
            if (sep === '?' && trailing === '') return '';
            if (sep === '&' && trailing === '') return '';
            return sep === '&' ? '' : sep;
        }).replace(/\?$/, '');
    }

    return newUrl;
}

function clearSearchPopupVariantSelection() {
    if (!document.documentElement.hasAttribute('data-stt-no-variant')) return;

    document.querySelectorAll('select[name^="option"]').forEach(function(select) {
        if (select.options.length <= 1) return;

        var blank = select.querySelector('option[value=""]');
        if (!blank) {
            blank = document.createElement('option');
            blank.value = '';
            blank.textContent = 'Choose an option';
            select.insertBefore(blank, select.firstChild);
        }

        select.value = '';

        var box = select.closest('combo-box');
        if (box) {
            box.querySelectorAll('[role="option"]').forEach(function(button) {
                button.setAttribute('aria-selected', 'false');
            });
        }

        var label = document.getElementById(select.getAttribute('data-bind-value'));
        if (label) label.textContent = 'Choose an option';
    });

    var VariantPicker = customElements.get('product-variants');
    if (!VariantPicker || VariantPicker.prototype.__sttNoAutoVariant) return;

    var originalSelectVariant = VariantPicker.prototype.selectVariant;
    VariantPicker.prototype.selectVariant = function(id) {
        if (document.documentElement.hasAttribute('data-stt-no-variant')) {
            var options = this._getSelectedOptionValues();
            if (options.some(function(value) { return !value; })) return;
        }

        return originalSelectVariant.call(this, id);
    };
    VariantPicker.prototype.__sttNoAutoVariant = true;
}

clearSearchPopupVariantSelection();

function rewriteProductLinks(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('a').forEach(ele => {
        const oldUrl = ele.getAttribute('href');
        if (!oldUrl || oldUrl.indexOf('/products/') === -1) return;

        const newUrl = productUrlWithoutVariant(oldUrl);
        if (newUrl === oldUrl) return;

        ele.setAttribute('href', newUrl);
        const newEle = ele.cloneNode(true);
        ele.replaceWith(newEle);
    });
}

function hooks() {
    window.SerpOptions.registerHook('serp-product-grid', ({element}) => {
        rewriteProductLinks(element);
    });
}

if (window.SerpOptions) {
    hooks();
} else {
    window.addEventListener('fast-serp-ready', function () {
        hooks();
    });
}

function registerAutocompleteVariantStrip() {
    if (!window.FastAutocomplete || window.FastAutocomplete.__sttVariantStrip) return;

    window.FastAutocomplete.registerHook('render-autocomplete-product-item', ({element, product}) => {
        if (product && typeof product.u === 'string') {
            let nextUrl = productUrlWithoutVariant(product.u);
            try {
                const url = new URL(nextUrl, window.location.origin);
                if (!url.searchParams.has('from_autocomplete')) {
                    url.searchParams.set('from_autocomplete', '1');
                }
                nextUrl = /^https?:\/\//i.test(nextUrl) ? url.toString() : (url.pathname + url.search + url.hash);
            } catch (e) {}
            product.u = nextUrl;
        }
        rewriteProductLinks(element);
    });
    window.FastAutocomplete.__sttVariantStrip = true;
}

if (window.FastAutocomplete) {
    registerAutocompleteVariantStrip();
} else {
    window.addEventListener('fast-autocomplete-ready', registerAutocompleteVariantStrip);
}

function isAutocompleteProductLink(link) {
    if (!link || link.tagName !== 'A') return false;

    const href = link.getAttribute('href') || '';
    if (href.indexOf('/products/') === -1) return false;
    if (href.indexOf('from_autocomplete') !== -1) return true;
    if (link.classList.contains('fast-ac-item') || link.classList.contains('fs-product')) return true;
    if (link.closest('.fast-autocomplete, .fast-wide-products, [class*="fast-simon"]')) return true;

    return false;
}

function autocompleteLinkFromEvent(event) {
    const path = event.composedPath ? event.composedPath() : [];
    for (let i = 0; i < path.length; i++) {
        const node = path[i];
        if (node && node.tagName === 'A' && isAutocompleteProductLink(node)) return node;
    }

    const link = event.target.closest && event.target.closest('a');
    return isAutocompleteProductLink(link) ? link : null;
}

function openAutocompleteProduct(event) {
    const link = autocompleteLinkFromEvent(event);
    if (!link) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    try { sessionStorage.setItem('stt-no-variant', '1'); } catch (e) {}

    const href = link.getAttribute('href') || link.href;
    window.location.assign(productUrlWithoutVariant(href));
}

['pointerdown', 'mousedown', 'click'].forEach(function(type) {
    document.addEventListener(type, openAutocompleteProduct, true);
});